import { noop, uniqueId } from 'lodash';
import {
  defer,
  finalize,
  map,
  tap,
  type MonoTypeOperatorFunction,
  type OperatorFunction,
} from 'rxjs';
import { currentTimestamp } from '@ardoq/date-time';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  activeViewRegistered,
  activeViewUnregistered,
  viewHoldReleased,
  viewHoldTaken,
} from './actions';
import type {
  HasDataWorkToken,
  ViewHoldKind,
  ViewStatusReporter,
  ViewWork,
} from './types';

export const createViewStatusReporter = (
  viewInstanceId: string
): ViewStatusReporter => ({
  register: () => dispatchAction(activeViewRegistered(viewInstanceId)),
  unregister: () => dispatchAction(activeViewUnregistered(viewInstanceId)),
  reportWorkStarted: ({ kind, token }) =>
    dispatchAction(
      viewHoldTaken({ viewInstanceId, kind, token, since: currentTimestamp() })
    ),
  reportWorkFinished: token =>
    dispatchAction(viewHoldReleased({ viewInstanceId, token })),
});

export const NOOP_VIEW_STATUS_REPORTER: ViewStatusReporter = {
  register: noop,
  unregister: noop,
  reportWorkStarted: noop,
  reportWorkFinished: noop,
};

/** Reports work of the kind as started and @returns its token, for work started outside a stream. */
export const startViewWork = (
  viewStatusReporter: ViewStatusReporter,
  kind: ViewHoldKind
): string => {
  const token = uniqueId(`${kind}-`);
  viewStatusReporter.reportWorkStarted({ kind, token });
  return token;
};

/** Keeps the view registered while the stream is subscribed; registers before subscribing to the source. */
export const registerWhileSubscribed =
  <T>(viewStatusReporter: ViewStatusReporter): MonoTypeOperatorFunction<T> =>
  source$ =>
    defer(() => {
      viewStatusReporter.register();
      return source$;
    }).pipe(finalize(viewStatusReporter.unregister));

const toDataWork = (stage: string, emissionIndex: number): ViewWork => ({
  kind: 'data',
  token: `data-${stage}-${emissionIndex}`,
});

/** Reports data work as started on every emission, e.g. when the source data resets ahead of a debounce. */
export const reportDataWorkStarted =
  <T>(
    viewStatusReporter: ViewStatusReporter,
    stage: string
  ): MonoTypeOperatorFunction<T> =>
  source$ =>
    source$.pipe(
      map((value, emissionIndex) => ({
        value,
        work: toDataWork(stage, emissionIndex),
      })),
      tap(({ work }) => viewStatusReporter.reportWorkStarted(work)),
      map(({ value }) => value)
    );

/** Starts data work for every emission and carries its token, for the view to finish once it has rendered it. */
export const withDataWork =
  <T extends object>(
    viewStatusReporter: ViewStatusReporter,
    stage: string
  ): OperatorFunction<T, T & HasDataWorkToken> =>
  source$ =>
    source$.pipe(
      map((value, emissionIndex) => ({
        value,
        work: toDataWork(stage, emissionIndex),
      })),
      tap(({ work }) => viewStatusReporter.reportWorkStarted(work)),
      map(({ value, work }) => ({ ...value, dataWorkToken: work.token }))
    );
